/**
 * Free-text recipe ingredient line parsing, shared by every place that turns
 * "3 tablespoons olive oil" into structured parts: the system catalog seed, the
 * catalog importer, and the web recipe editor.
 *
 * The three used to carry their own copy of the same regex, which took ANY
 * alphabetic word after the quantity as the unit. That is wrong whenever the
 * food's own name starts with a word — "2 celery stalks, diced" parsed to unit
 * "celery" / name "stalks, diced", and "1 bay leaf" to unit "bay" / name "leaf"
 * (#930). The mangled names then reached shopping lists, search, and the
 * assistant, because `name` is what everything downstream reads.
 *
 * The fix is a closed unit vocabulary: a token only becomes the unit if it is
 * one. Anything else stays part of the name, which is the safe direction —
 * "celery stalks, diced" is a worse unit split than "stalk" + "celery" but it is
 * still a correct, shoppable name, whereas "stalks, diced" is neither.
 */
/**
 * Words that can occupy the unit slot. Measurement units plus the container and
 * count nouns that genuinely precede the food in recipe prose ("1 can crushed
 * tomatoes", "3 cloves garlic"). Deliberately closed: adding a word here that
 * can also start a food name reintroduces #930 for that food.
 */
const unitWords = new Set([
    // volume
    "teaspoon", "teaspoons", "tsp", "tsps",
    // tblsp/tbls are the catalog's own spellings (122 and 20 lines); the old
    // any-word parser captured them, so omitting them here would have REGRESSED
    // those rows on the next reseed rather than fixed them.
    "tablespoon", "tablespoons", "tbsp", "tbsps", "tbs", "tblsp", "tblsps", "tbls", "tblspn",
    "cup", "cups", "c",
    "pint", "pints", "pt", "pts",
    "quart", "quarts", "qt", "qts",
    "gallon", "gallons", "gal", "gals",
    "milliliter", "milliliters", "millilitre", "millilitres", "ml",
    "liter", "liters", "litre", "litres", "l",
    "fluid", "floz",
    // weight
    "ounce", "ounces", "oz",
    "pound", "pounds", "lb", "lbs",
    "gram", "grams", "g",
    "kilogram", "kilograms", "kg",
    "milligram", "milligrams", "mg",
    // containers and counts
    "can", "cans",
    "package", "packages", "pkg", "pkgs",
    "pack", "packs",
    "packet", "packets",
    "pot", "pots",
    "bowl", "bowls",
    "pod", "pods",
    "floret", "florets",
    "rasher", "rashers",
    "leaf", "leaves",
    "part", "parts",
    "inch", "inches",
    "bag", "bags",
    "box", "boxes",
    "jar", "jars",
    "bottle", "bottles",
    "container", "containers",
    "carton", "cartons",
    "tub", "tubs",
    "bunch", "bunches",
    "clove", "cloves",
    "head", "heads",
    "sprig", "sprigs",
    "stick", "sticks",
    "slice", "slices",
    "piece", "pieces",
    "sheet", "sheets",
    "strip", "strips",
    "loaf", "loaves",
    "ear", "ears",
    "fillet", "fillets",
    "bulb", "bulbs",
    "knob", "knobs",
    // Safe in the "1 stalk celery" position only. The "2 celery stalks" case that
    // motivated this vocabulary is unaffected: "celery" is the first word there,
    // and it is not a unit, so the whole phrase stays in the name.
    "stalk", "stalks",
    "tin", "tins",
    "scoop", "scoops",
    "pinch", "pinches",
    "dash", "dashes",
    "drop", "drops",
    // The catalog's own misspellings count: the old any-word parser split them
    // correctly, so omitting them would regress those rows on reseed.
    "handful", "handfuls", "handfull", "handfulls"
]);
/**
 * Two-word units, matched before the single-token pass so "1 fluid ounce
 * vanilla" does not stop at "fluid".
 */
const multiWordUnits = [
    ["fluid", "ounce"],
    ["fluid", "ounces"]
];
export function isIngredientUnit(word) {
    return unitWords.has(word.trim().toLowerCase());
}
/**
 * Words that start a trailing preparation clause. A recipe writes what to do
 * with the food after a comma ("yellow onion, diced"), but a shopping list wants
 * the food (#933) — nobody buys "diced".
 */
const prepClauseLeads = new Set([
    "diced", "chopped", "minced", "sliced", "peeled", "trimmed", "torn", "drained", "rinsed",
    "softened", "melted", "cut", "halved", "quartered", "crushed", "grated", "shredded",
    "beaten", "divided", "cubed", "julienned", "seeded", "stemmed", "pitted", "deveined",
    "thinly", "finely", "roughly", "coarsely", "freshly", "lightly", "well",
    "at", "about", "optional", "packed", "warmed", "chilled", "room", "any"
]);
/**
 * "plus" introduces a hedge ("plus more for the pan") far more often than a
 * second ingredient, but not always: "1 large egg, plus 1 large egg yolk" is two
 * things to buy, and dropping the tail silently loses the yolk and its quantity.
 * Only the hedge forms are throwaway.
 */
const plusQualifier = /^plus\s+(more|extra)\b/;
/**
 * Deliberately NOT prep leads, because each introduces something you still have
 * to buy rather than something done to it:
 * - "or"        — an alternative food ("salted cod, or bakijow"), 81 rows;
 * - "preferably"— a purchase constraint ("ground beef, preferably 80/20");
 * - "such"      — an example of what to buy ("green olives, such as Castelvetrano").
 * Only the quantity hedge behind "or" is a throwaway.
 */
const orQualifier = /^or\s+(more|less|to\s+taste)\b/;
/**
 * Trailing parentheticals worth dropping. A terminal parenthetical can just as
 * easily be a required product qualifier — "plain Greek yogurt (whole milk or
 * 2%)", "cream (for whipping)", "rice (for risotto)" all say which one to buy —
 * so only unambiguous serving/garnish roles and hedges go.
 *
 * "(for <component>)" is deliberately an enumerated list rather than a pattern.
 * A component label ("for tzatziki", "for the marinade") and a purchase
 * qualifier ("for whipping", "for risotto") are lexically identical — both are
 * "for <noun>" — and guessing wrong in the qualifier direction loses what to
 * buy, which is the worse failure. New labels get added as the catalog shows
 * them, not by widening the pattern.
 */
const qualifierParenthetical = /\s*\((?:optional|or\s+more|or\s+less|to\s+taste|for\s+(?:serving|garnish|topping|drizzling|dusting|greasing|dredging|brushing|sprinkling|the\s+[a-z ]+|marinade|sauce|gravy|filling|dough|crust|batter|dressing|glaze|topping|coating|frosting|assembly|tzatziki|chimichurri|color|colour|egg\s+wash)|divided|plus\s+more[^()]*)\)\s*$/i;
/**
 * The shoppable form of an ingredient name: preparation clauses and garnish
 * qualifiers removed, the food itself kept.
 *
 * Only trailing clauses that actually look like preparation are cut, so
 * "salt, pepper" (two foods) survives while "carrots, peeled and diced" loses
 * its instructions. Returns the original text when stripping would leave
 * nothing.
 */
/**
 * Index of the last comma OUTSIDE any parentheses, or -1.
 *
 * Scanning the raw string would reach inside a parenthetical that the qualifier
 * rules deliberately preserve: "cayenne pepper (optional, for heat)" has a comma
 * at depth 1, and cutting there leaves the unbalanced "cayenne pepper (optional"
 * — 30 catalog rows do this.
 */
function lastTopLevelComma(value) {
    let depth = 0;
    let found = -1;
    for (let index = 0; index < value.length; index += 1) {
        const char = value[index];
        if (char === "(")
            depth += 1;
        else if (char === ")")
            depth = Math.max(0, depth - 1);
        else if (char === "," && depth === 0)
            found = index;
    }
    return found;
}
function stripTrailingClauses(name, isDroppable) {
    let result = name.trim().replace(/\s+/g, " ");
    // Only known throwaway parentheticals, and only at the end: a LEADING one can
    // be part of the food ("(28 oz) can crushed tomatoes").
    result = result.replace(qualifierParenthetical, "").trim();
    for (;;) {
        const comma = lastTopLevelComma(result);
        if (comma <= 0)
            break;
        const tail = result.slice(comma + 1).trim().toLowerCase();
        const lead = tail.split(/\s+/)[0] ?? "";
        if (!isDroppable(tail, lead))
            break;
        result = result.slice(0, comma).trim();
        // Removing a tail can expose a NEW terminal parenthetical, so the qualifier
        // rules get another look rather than only running once at the top.
        result = result.replace(qualifierParenthetical, "").trim();
    }
    return result || name.trim();
}
/**
 * The name to match an ingredient against the household's foods.
 *
 * Strips ONLY unambiguous preparation ("garlic, minced" -> "garlic"), never a
 * qualifier that says which product to buy. {@link shoppableIngredientName}
 * additionally drops "for ..."/"to ..." tails, which is right for a list label
 * but wrong here: "cream, for whipping" would fold onto a generic "Cream" and
 * let that stock suppress the whipping cream the recipe actually needs. A missed
 * match just means buying it; a false match means not buying something you need,
 * and attributing one food's stock to another (#697).
 */
export function stockMatchIngredientName(name) {
    return stripTrailingClauses(name, (tail, lead) => !tail || prepClauseLeads.has(lead));
}
export function shoppableIngredientName(name) {
    return stripTrailingClauses(name, (tail, lead) => !tail
        || prepClauseLeads.has(lead)
        || orQualifier.test(tail)
        || plusQualifier.test(tail)
        || tail.startsWith("to ")
        || tail.startsWith("for "));
}
/** Leading quantity: a decimal, a fraction ("1/2"), or a mixed number ("1 1/2"). */
const quantityPattern = /^(\d+\s+\d+\/\d+|\d+\/\d+|\d+(?:\.\d+)?)\s+(.*)$/;
function quantityValue(text) {
    const mixed = text.match(/^(\d+)\s+(\d+)\/(\d+)$/);
    if (mixed) {
        const whole = Number(mixed[1]);
        const denominator = Number(mixed[3]);
        if (!denominator)
            return null;
        const value = whole + Number(mixed[2]) / denominator;
        return Number.isFinite(value) ? value : null;
    }
    const fraction = text.match(/^(\d+)\/(\d+)$/);
    if (fraction) {
        const denominator = Number(fraction[2]);
        if (!denominator)
            return null;
        const value = Number(fraction[1]) / denominator;
        return Number.isFinite(value) ? value : null;
    }
    const value = Number(text);
    return Number.isFinite(value) ? value : null;
}
/**
 * Split one ingredient line into quantity, unit, and name. A line with no
 * leading quantity ("salt and pepper to taste") keeps its whole text as the
 * name, which is what every caller wants to show and to shop for.
 */
export function parseIngredientLine(raw) {
    const cleaned = raw.trim().replace(/\s+/g, " ");
    const match = cleaned.match(quantityPattern);
    if (!match)
        return { raw: cleaned, name: cleaned, quantity: null, unit: "" };
    const quantity = quantityValue(match[1] ?? "");
    let rest = (match[2] ?? "").trim();
    if (quantity == null || !rest)
        return { raw: cleaned, name: cleaned, quantity: null, unit: "" };
    let unit = "";
    const words = rest.split(" ");
    const wordAt = (index) => (words[index] ?? "").replace(/\.$/, "").toLowerCase();
    // Multiplier notation: "2 x 400g barramundi" is two of the thing, and the "x"
    // is punctuation rather than a unit or part of the name.
    if (wordAt(0) === "x" && words.length > 1)
        words.splice(0, 1);
    // Two-word units first, so "fluid" never wins alone. A unit is only consumed
    // when something is left to name the food with: "2 cups" on its own is a food
    // called "cups", not a bare unit.
    const isMultiWord = multiWordUnits.some((pair) => pair[0] === wordAt(0) && pair[1] === wordAt(1));
    if (isMultiWord) {
        // Exclusive on purpose. Falling through on "1 fluid ounce" (nothing left to
        // name) would let the single-token branch consume "fluid" alone and hand
        // back unit "fluid" / name "ounce" — the exact split the vocabulary exists
        // to prevent.
        if (words.length > 2) {
            unit = `${words[0]} ${words[1]}`;
            words.splice(0, 2);
        }
    }
    else if (isIngredientUnit(wordAt(0)) && words.length > 1) {
        unit = (words[0] ?? "").replace(/\.$/, "");
        words.splice(0, 1);
    }
    // Recipes write count units both ways: "1 head cauliflower" and "1 head of
    // lettuce". Without this the connective became the food's first word.
    if (unit && wordAt(0) === "of" && words.length > 1)
        words.splice(0, 1);
    rest = words.join(" ").trim();
    return { raw: cleaned, name: rest || cleaned, quantity, unit };
}
